import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CATEGORY_META } from "@/mocks/films";

const NAV_LINKS = [
  { to: "/", label: "首页" },
  { to: "/category/drama", label: "分类" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200"
          : "bg-primary-950 border-primary-900"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span
              className={`w-9 h-9 rounded-md flex items-center justify-center text-lg transition-colors ${
                scrolled ? "bg-primary-900 text-accent-400" : "bg-accent-500 text-primary-950"
              }`}
            >
              <i className="ri-clapperboard-line" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-base md:text-lg font-semibold tracking-wide ${
                  scrolled ? "text-foreground-950" : "text-background-50"
                }`}
              >
                欧美成人电影
              </span>
              <span
                className={`hidden sm:block text-[10px] tracking-[0.28em] uppercase mt-1 ${
                  scrolled ? "text-foreground-500" : "text-background-300"
                }`}
              >
                Cinema Archive
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? scrolled
                        ? "bg-primary-900 text-background-50"
                        : "bg-background-50/15 text-background-50"
                      : scrolled
                        ? "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
                        : "text-background-200 hover:text-background-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center relative">
              <i
                className={`ri-search-line absolute left-3 text-sm ${
                  scrolled ? "text-foreground-400" : "text-background-300"
                }`}
              />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索片名或演员"
                aria-label="搜索影片"
                className={`w-52 xl:w-64 h-9 pl-9 pr-3 rounded-full text-sm outline-none transition-all focus:w-72 ${
                  scrolled
                    ? "bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
                    : "bg-background-50/10 border border-background-50/20 text-background-50 placeholder:text-background-300/80 focus:bg-background-50/15 focus:border-accent-400"
                }`}
              />
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="打开搜索"
              className={`lg:hidden w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:bg-background-200"
                  : "text-background-100 hover:bg-background-50/10"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:bg-background-200"
                  : "text-background-100 hover:bg-background-50/10"
              }`}
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="lg:hidden pb-3 animate-fade-in">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索片名或演员"
                  className="w-full h-10 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-accent-500"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-accent-600"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-2 pt-3 border-t border-background-200">
              <p className="px-3 pb-2 text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                热门分类
              </p>
              <div className="grid grid-cols-2 gap-1">
                {CATEGORY_META.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 flex items-center gap-2"
                  >
                    <i className={`${cat.icon} text-accent-600`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}